import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { useCurrency } from '../hooks/useCurrency';
import { toast } from 'react-toastify';
import GoldConfetti from '../components/GoldConfetti';

const COIN_META = {
  BTC:  { name: 'Bitcoin',  decimals: 8, glyph: '₿', tint: 'from-amber-400 to-orange-500' },
  ETH:  { name: 'Ethereum', decimals: 8, glyph: 'Ξ', tint: 'from-indigo-400 to-violet-600' },
  LTC:  { name: 'Litecoin', decimals: 8, glyph: 'Ł', tint: 'from-slate-300 to-slate-500' },
  USDT: { name: 'Tether',   decimals: 2, glyph: '₮', tint: 'from-blue-300 to-blue-600' },
};

function Countdown({ expiresAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!expiresAt) return null;
  const ms = Math.max(0, new Date(expiresAt).getTime() - now);
  const h = String(Math.floor(ms / 3600000)).padStart(2, '0');
  const m = String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0');
  const s = String(Math.floor((ms % 60000) / 1000)).padStart(2, '0');
  const expired = ms === 0;
  return (
    <div className={'font-mono text-3xl tracking-widest tabular-nums ' + (expired ? 'text-red-400' : 'text-amber-200')}>
      {expired ? 'EXPIRED' : `${h}:${m}:${s}`}
    </div>
  );
}

function CopyRow({ label, value, mono = true }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };
  return (
    <div className="group flex items-center gap-3 rounded-2xl border border-amber-200/15 bg-white/5 backdrop-blur px-5 py-4 hover:border-amber-200/30 transition-colors">
      <span className="text-[10px] uppercase tracking-[0.2em] text-amber-200/70 shrink-0 w-12">{label}</span>
      <span className={'flex-1 truncate text-white ' + (mono ? 'font-mono text-sm' : 'text-sm')}>{value}</span>
      <button
        type="button"
        onClick={onCopy}
        className="text-xs font-semibold uppercase tracking-wider text-amber-200 hover:text-amber-100 px-2 py-1 rounded border border-amber-200/30 hover:border-amber-200/60 transition-colors"
      >
        {copied ? '✓ Copied' : 'Copy'}
      </button>
    </div>
  );
}

// ─── Order chat panel ────────────────────────────────────────────────────────
function OrderChat({ orderId, onConfirmFire }) {
  const [messages, setMessages]   = useState([]);
  const [adminOnline, setAdminOnline] = useState(false);
  const [draft, setDraft]         = useState('');
  const [sending, setSending]     = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const scrollerRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/messages`);
      setMessages(data.messages || []);
      setAdminOnline(!!data.adminOnline);
    } catch (err) {
      // Silent — chat is non-blocking.
    }
  }, [orderId]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 8000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/messages`, { body: text });
      setMessages(m => [...m, data]);
      setDraft('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const confirmPayment = async () => {
    if (confirming || confirmed) return;
    setConfirming(true);
    try {
      await api.post(`/orders/${orderId}/confirm-payment`, {});
      setConfirmed(true);
      // Optimistically append a confirmation bubble.
      setMessages(m => [...m, {
        id: `tmp-${Date.now()}`,
        senderType: 'customer',
        body: 'I have sent the payment. Please confirm on your end.',
        isPaymentClaim: true,
        createdAt: new Date().toISOString(),
        author: null,
      }]);
      onConfirmFire?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not confirm payment');
      setConfirming(false);
    }
  };

  return (
    <div className="rounded-3xl border border-amber-300/20 bg-gradient-to-br from-white/[0.05] to-transparent backdrop-blur p-7 flex flex-col">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-amber-200/80">Talk to our team</div>
          <div className="text-white/60 text-xs mt-1">Tell us once you've sent the payment — we'll confirm and ship.</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={'relative flex h-2.5 w-2.5 ' + (adminOnline ? '' : 'opacity-60')}>
            {adminOnline && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-70" />
            )}
            <span className={'relative inline-flex h-2.5 w-2.5 rounded-full ' + (adminOnline ? 'bg-blue-400' : 'bg-white/30')} />
          </span>
          <span className={'text-[10px] uppercase tracking-[0.3em] ' + (adminOnline ? 'text-blue-300' : 'text-white/40')}>
            {adminOnline ? 'Agent online' : 'Offline · we reply within hours'}
          </span>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="relative flex-1 max-h-72 min-h-[12rem] overflow-y-auto rounded-2xl border border-white/10 bg-black/40 p-4 space-y-3 mb-4"
      >
        {messages.length === 0 && (
          <div className="text-center text-white/40 text-xs italic py-8">
            No messages yet. Drop us a note any time — we'll see it as soon as we're back.
          </div>
        )}
        {messages.map(m => {
          const mine = m.senderType === 'customer';
          return (
            <div key={m.id} className={'flex ' + (mine ? 'justify-end' : 'justify-start')}>
              <div className={
                'max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm ' +
                (mine
                  ? 'bg-gradient-to-br from-amber-300/90 to-amber-500/90 text-black rounded-br-sm'
                  : m.senderType === 'system'
                    ? 'bg-white/5 text-white/60 italic border border-white/10'
                    : 'bg-white/10 text-white border border-amber-200/20 rounded-bl-sm')
              }>
                {!mine && (
                  <div className="text-[10px] uppercase tracking-[0.2em] mb-1 text-amber-200/80">
                    {m.senderType === 'admin' ? (m.author?.firstName || 'Aloha Amino team') : 'System'}
                  </div>
                )}
                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                {m.isPaymentClaim && (
                  <div className={'mt-1 text-[10px] uppercase tracking-[0.2em] ' + (mine ? 'text-black/60' : 'text-amber-200/80')}>
                    ✦ Payment confirmation
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-end gap-2 mb-3">
        <textarea
          rows={2}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Message our admins…"
          className="flex-1 resize-none rounded-2xl bg-white/5 border border-white/10 focus:border-amber-300/40 focus:outline-none focus:ring-1 focus:ring-amber-300/30 text-white text-sm placeholder-white/30 px-4 py-2.5"
          maxLength={2000}
          disabled={confirmed}
        />
        <button
          type="button"
          onClick={send}
          disabled={!draft.trim() || sending || confirmed}
          className="shrink-0 px-5 py-2.5 rounded-2xl border border-amber-300/40 text-amber-200 hover:bg-amber-300/10 hover:border-amber-300/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold uppercase tracking-wider"
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>

      <button
        type="button"
        onClick={confirmPayment}
        disabled={confirming || confirmed}
        className="group relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 text-black font-semibold tracking-wide py-3.5 px-5 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        <span className="relative flex items-center justify-center gap-2 uppercase tracking-[0.2em] text-xs sm:text-sm">
          {confirmed ? (
            <>✦ Confirmation received</>
          ) : confirming ? (
            <>Confirming…</>
          ) : (
            <>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 13l4 4L19 7" />
              </svg>
              I've made the payment
            </>
          )}
        </span>
      </button>
      <div className="mt-2 text-center text-[10px] uppercase tracking-[0.3em] text-white/30">
        Our team verifies on-chain before fulfilling
      </div>
    </div>
  );
}

export default function CryptoPaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fmt } = useCurrency();

  const [order, setOrder]       = useState(null);
  const [methods, setMethods]   = useState([]);
  const [prices, setPrices]     = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [chosenKey, setChosenKey] = useState(null);
  const [error, setError]       = useState('');

  // Celebration overlay state
  const [celebrating, setCelebrating] = useState(false);
  const [fadingOut, setFadingOut]     = useState(false);

  const pollRef = useRef(null);

  const loadOrder = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      if (['paid', 'processing', 'shipped', 'delivered'].includes(data.status)) {
        if (pollRef.current) clearInterval(pollRef.current);
        toast.success('Payment confirmed!');
        setTimeout(() => navigate(`/thank-you/${id}`), 1200);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load order');
    }
  }, [id, navigate]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  useEffect(() => {
    (async () => {
      try {
        const [m, p] = await Promise.all([
          api.get('/orders/crypto/methods'),
          api.get('/orders/crypto/prices'),
        ]);
        setMethods(m.data);
        setPrices(p.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load payment options');
      }
    })();
  }, []);

  useEffect(() => {
    if (!order?.cryptoCurrency) return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(loadOrder, 15000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [order?.cryptoCurrency, loadOrder]);

  const selectCoin = async (currency, network) => {
    setSelecting(true);
    setError('');
    try {
      const { data } = await api.post(`/orders/${id}/crypto/select`, { currency, network });
      setChosenKey(`${currency}-${network || ''}`);
      await loadOrder();
      toast.info(`Send ${data.amount} ${currency}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to select payment method');
    } finally {
      setSelecting(false);
    }
  };

  const cancelOrder = async () => {
    if (!window.confirm('Cancel this order?')) return;
    setCancelling(true);
    try {
      await api.post(`/orders/${id}/crypto/cancel`);
      toast.success('Order cancelled');
      navigate('/account');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  // Confirm-payment flow: confetti → fade → dashboard
  const onConfirmFire = () => {
    setCelebrating(true);
    // Begin fade after the burst peaks
    setTimeout(() => setFadingOut(true), 2600);
    // Navigate away after the fade completes
    setTimeout(() => navigate('/dashboard'), 4200);
  };

  // ── Loading / error states ─────────────────────────────────────────────
  if (error && !order) {
    return (
      <Shell>
        <div className="text-center py-24">
          <h1 className="text-3xl font-light text-amber-100 mb-3">Unable to load order</h1>
          <p className="text-white/60 mb-8">{error}</p>
          <Link to="/account" className="inline-block px-8 py-3 rounded-full border border-amber-300/40 text-amber-200 hover:bg-amber-300/10 transition-colors">
            Go to my account
          </Link>
        </div>
      </Shell>
    );
  }

  if (!order) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-32 text-white/40">
          <div className="animate-pulse text-sm tracking-[0.3em] uppercase">Preparing your invoice…</div>
        </div>
      </Shell>
    );
  }

  if (order.paymentMethod !== 'crypto') {
    return (
      <Shell>
        <div className="text-center py-24">
          <h1 className="text-3xl font-light text-amber-100 mb-3">This isn't a crypto order</h1>
          <Link to={`/order-success/${id}`} className="inline-block mt-4 px-8 py-3 rounded-full border border-amber-300/40 text-amber-200 hover:bg-amber-300/10 transition-colors">
            View order
          </Link>
        </div>
      </Shell>
    );
  }

  const hasSelection = Boolean(order.cryptoCurrency && order.cryptoAddress && order.cryptoAmount);
  const cryptoUri = hasSelection
    ? ((order.cryptoCurrency === 'BTC' || order.cryptoCurrency === 'LTC')
        ? `${order.cryptoCurrency.toLowerCase()}:${order.cryptoAddress}?amount=${order.cryptoAmount}`
        : order.cryptoAddress)
    : '';
  const qrSrc = hasSelection
    ? `https://api.qrserver.com/v1/create-qr-code/?size=260x260&bgcolor=ffffff&color=0B0B0B&margin=10&data=${encodeURIComponent(cryptoUri)}`
    : '';

  return (
    <Shell fadingOut={fadingOut}>
      <GoldConfetti active={celebrating} />

      {/* Celebration banner — overlays once payment confirmed */}
      {celebrating && (
        <div className="pointer-events-none fixed inset-0 z-[90] flex items-center justify-center">
          <div className="text-center px-6 animate-[fadeInUp_500ms_ease-out_forwards]">
            <div className="text-7xl mb-4 drop-shadow-[0_0_30px_rgba(245,194,66,0.7)]">✦</div>
            <h2 className="font-light text-4xl md:text-5xl text-amber-100 mb-2 tracking-tight">
              Order received
            </h2>
            <p className="text-amber-200/80 text-sm tracking-[0.3em] uppercase">
              Thank you — taking you back to your dashboard
            </p>
          </div>
        </div>
      )}

      {/* Step header */}
      <div className="flex items-center justify-center gap-3 text-[10px] tracking-[0.4em] uppercase text-white/40 mb-10">
        <span>Cart</span>
        <span className="text-amber-300/50">◆</span>
        <span>Checkout</span>
        <span className="text-amber-300/50">◆</span>
        <span className="text-amber-200">Settlement</span>
      </div>

      {/* Big hero label */}
      <header className="text-center mb-12">
        <div className="inline-block px-4 py-1.5 rounded-full border border-amber-300/30 text-amber-200/90 text-[10px] uppercase tracking-[0.3em] mb-5">
          Order #{order.id.slice(0, 8).toUpperCase()}
        </div>
        <h1 className="font-light text-white text-4xl md:text-5xl tracking-tight mb-3">
          {hasSelection ? 'Complete your payment' : 'Choose your cryptocurrency'}
        </h1>
        <p className="text-white/50 text-sm max-w-xl mx-auto">
          {hasSelection
            ? 'Send the exact amount shown below to the address. We confirm receipt automatically — usually within minutes of network confirmation.'
            : 'We accept four major chains. Pick your preferred coin and we will lock the rate at the moment of selection.'}
        </p>
        <div className="mt-6 inline-flex items-center gap-3 text-amber-100">
          <span className="text-xs uppercase tracking-[0.3em] text-white/40">Total due</span>
          <span className="text-2xl font-light">{fmt(order.totalInCents)}</span>
          <span className="text-xs uppercase tracking-wider text-white/40">USD</span>
        </div>
      </header>

      {/* ── Step 1: coin picker ──────────────────────────────────────── */}
      {!hasSelection && (
        <section className="max-w-4xl mx-auto">
          {methods.length === 0 ? (
            <div className="rounded-3xl border border-amber-300/20 bg-white/5 backdrop-blur p-10 text-center">
              <p className="text-amber-200/80 italic">
                No cryptocurrency wallets are currently configured. Please contact support or choose card payment at checkout.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {methods.map(m => {
                const meta = COIN_META[m.currency] || { name: m.currency, decimals: 6, glyph: '◈', tint: 'from-amber-300 to-amber-500' };
                const rate = prices?.prices?.[m.currency];
                const cryptoAmt = rate ? (order.totalInCents / 100 / rate) : null;
                const key = `${m.currency}-${m.network || ''}`;
                const isLoading = selecting && chosenKey === key;
                return (
                  <button
                    key={m.id}
                    type="button"
                    disabled={selecting}
                    onClick={() => { setChosenKey(key); selectCoin(m.currency, m.network); }}
                    className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur p-6 text-left transition-all hover:border-amber-300/40 hover:from-white/[0.12] hover:to-white/[0.04] disabled:opacity-50"
                  >
                    {/* Glow */}
                    <div className={`absolute -top-12 -right-12 h-40 w-40 rounded-full bg-gradient-to-br ${meta.tint} opacity-20 blur-3xl group-hover:opacity-40 transition-opacity`} />

                    <div className="relative flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className={`text-4xl bg-gradient-to-br ${meta.tint} bg-clip-text text-transparent`}>{meta.glyph}</span>
                          <div>
                            <div className="text-white font-medium text-lg">{m.label || meta.name}</div>
                            <div className="text-xs uppercase tracking-[0.2em] text-white/40">
                              {m.currency}{m.network ? ` · ${m.network}` : ''}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-amber-300/60 text-xl group-hover:text-amber-200 group-hover:translate-x-1 transition-all">
                        {isLoading ? '…' : '→'}
                      </div>
                    </div>

                    <div className="relative mt-6 pt-4 border-t border-white/10">
                      {cryptoAmt !== null ? (
                        <>
                          <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-1">You'll send</div>
                          <div className="font-mono text-2xl text-amber-100">
                            {cryptoAmt.toFixed(COIN_META[m.currency]?.decimals ?? 6)}
                            <span className="text-white/40 text-sm ml-2">{m.currency}</span>
                          </div>
                          <div className="text-xs text-white/40 mt-1">
                            1 {m.currency} = {fmt(Math.round(rate * 100))}
                          </div>
                        </>
                      ) : (
                        <div className="text-white/40 text-sm italic">Rate loading…</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {error && (
            <p className="mt-6 text-center text-red-300/90 text-sm">{error}</p>
          )}

          {prices?.fetchedAt && (
            <p className="text-center text-[10px] uppercase tracking-[0.3em] text-white/30 mt-8">
              Rates synced {Math.round((Date.now() - prices.fetchedAt) / 60000)} min ago · refreshes hourly
              {prices.stale && ' · using cached value'}
            </p>
          )}

          <div className="mt-12 flex justify-center">
            <button
              type="button"
              onClick={cancelOrder}
              disabled={cancelling}
              className="text-xs uppercase tracking-[0.3em] text-white/40 hover:text-white/70 transition-colors disabled:opacity-50"
            >
              {cancelling ? 'Cancelling…' : 'Cancel order'}
            </button>
          </div>
        </section>
      )}

      {/* ── Step 2: payment instructions ─────────────────────────────── */}
      {hasSelection && (
        <section className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: address + amount */}
          <div className="lg:col-span-3 rounded-3xl border border-amber-300/20 bg-gradient-to-br from-white/[0.06] to-transparent backdrop-blur p-8">
            <div className="flex items-center gap-3 mb-6">
              <span className={'text-5xl bg-gradient-to-br ' + (COIN_META[order.cryptoCurrency]?.tint || 'from-amber-300 to-amber-500') + ' bg-clip-text text-transparent'}>
                {COIN_META[order.cryptoCurrency]?.glyph || '◈'}
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-amber-200/70">Paying with</div>
                <div className="text-white text-xl font-light">
                  {COIN_META[order.cryptoCurrency]?.name || order.cryptoCurrency}
                  {order.cryptoNetwork && <span className="text-white/40 text-sm ml-2">· {order.cryptoNetwork}</span>}
                </div>
              </div>
            </div>

            <div className="space-y-3 mb-8">
              <CopyRow label="Send" value={`${order.cryptoAmount} ${order.cryptoCurrency}`} />
              <CopyRow label="To"   value={order.cryptoAddress} />
            </div>

            <a
              href={cryptoUri}
              className="group relative inline-flex items-center gap-4 px-8 py-4 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-black font-semibold tracking-wide hover:shadow-2xl hover:shadow-amber-500/30 transition-all"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 3h7v7M21 3l-9 9M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
              </svg>
              <span>OPEN IN WALLET</span>
              <span className="text-black/60 group-hover:translate-x-1 transition-transform">→</span>
            </a>

            {order.cryptoRateUsd && (
              <div className="mt-8 pt-6 border-t border-white/10 grid grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">Locked rate</div>
                  <div className="text-white">1 {order.cryptoCurrency} = {fmt(Math.round(order.cryptoRateUsd * 100))}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">Total USD</div>
                  <div className="text-white">{fmt(order.totalInCents)}</div>
                </div>
                <p className="col-span-2 text-[11px] text-white/40 italic">
                  Settlement is in {order.cryptoCurrency || 'cryptocurrency'} at the locked rate above.
                </p>
              </div>
            )}
          </div>

          {/* Right: QR + countdown */}
          <div className="lg:col-span-2 rounded-3xl border border-amber-300/20 bg-black/40 backdrop-blur p-8 flex flex-col items-center text-center">
            <div className="relative mb-5">
              <div className="absolute -inset-3 bg-gradient-to-br from-amber-300/30 to-transparent rounded-3xl blur-xl" />
              <img
                src={qrSrc}
                alt="Scan to pay"
                className="relative w-56 h-56 rounded-2xl border border-amber-300/30 bg-white p-3"
              />
            </div>

            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">Awaiting payment</div>
            <Countdown expiresAt={order.cryptoExpiresAt} />
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mt-2">Status checked every 15s</div>

            <button
              type="button"
              onClick={cancelOrder}
              disabled={cancelling}
              className="mt-8 text-xs uppercase tracking-[0.3em] text-white/40 hover:text-red-300 transition-colors disabled:opacity-50"
            >
              {cancelling ? 'Cancelling…' : 'Cancel order'}
            </button>
          </div>

          {/* Full-width: chat panel */}
          <div className="lg:col-span-5">
            <OrderChat orderId={order.id} onConfirmFire={onConfirmFire} />
          </div>

          {/* Full-width: order summary */}
          <div className="lg:col-span-5 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur p-8">
            <h3 className="text-xs uppercase tracking-[0.3em] text-amber-200/80 mb-5">Order details</h3>
            <div className="divide-y divide-white/5 text-sm">
              {(order.items || []).map(item => (
                <div key={item.id} className="flex items-center justify-between py-3">
                  <span className="text-white/80">
                    {item.variant?.product?.name} — {item.variant?.name}
                    <span className="text-white/40"> × {item.quantity}</span>
                  </span>
                  <span className="text-white">{fmt(item.totalPriceInCents)}</span>
                </div>
              ))}
              <Line label="Subtotal" value={fmt(order.subtotalInCents)} />
              <Line label="Shipping" value={order.shippingInCents === 0 ? 'FREE' : fmt(order.shippingInCents)} />
              {order.discountInCents > 0 && (
                <Line label="Discount" value={'− ' + fmt(order.discountInCents)} />
              )}
              <div className="flex items-center justify-between py-4">
                <span className="text-amber-200 uppercase tracking-[0.2em] text-xs">Total</span>
                <span className="text-2xl font-light text-amber-100">{fmt(order.totalInCents)}</span>
              </div>
            </div>
          </div>
        </section>
      )}
    </Shell>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="text-white/50">{label}</span>
      <span className="text-white">{value}</span>
    </div>
  );
}

// ─── Shell: black-and-gold premium frame ──────────────────────────────────────
function Shell({ children, fadingOut = false }) {
  return (
    <div
      className={'relative min-h-screen text-white overflow-hidden transition-all duration-[1400ms] ' + (fadingOut ? 'opacity-0 scale-[0.98] blur-sm' : 'opacity-100')}
      style={{ background: 'radial-gradient(ellipse at top, #1a1410 0%, #0a0805 55%, #050302 100%)' }}
    >
      {/* Subtle gold orbs */}
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[480px] w-[820px] rounded-full bg-amber-500/10 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[320px] w-[320px] rounded-full bg-amber-700/10 blur-[120px]" />

      {/* Top bar */}
      <div className="relative border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/shop" className="flex items-center gap-2 text-amber-200 hover:text-amber-100 transition-colors">
            <span className="text-2xl">✦</span>
            <span className="font-light tracking-[0.3em] uppercase text-sm">Aloha Amino</span>
          </Link>
          <span className="text-[10px] uppercase tracking-[0.4em] text-white/40">Secure crypto settlement</span>
        </div>
      </div>

      <main className="relative max-w-6xl mx-auto px-6 py-14">
        {children}
      </main>

      <footer className="relative border-t border-white/5 mt-16">
        <div className="max-w-6xl mx-auto px-6 py-6 text-center text-[10px] uppercase tracking-[0.4em] text-white/30">
          Encrypted · Non-custodial · Final on-chain
        </div>
      </footer>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
